/** The client's cache of state packs, keyed by the pack's NAME (its content
 *  address: `snapshots/v2/<island>/packs/<fs>/<sha256>.ndjson.gz`, wire
 *  contract §2.1). A name never names other bytes, so a cached pack is never
 *  stale; a later commit fetches only the packs it does not hold.
 *
 *  The persistent store (below) is per (user, organisation); see its rules. Every failure — no IndexedDB (a private window, a test),
 *  quota, a broken database — degrades to "not cached": the loader fetches. */

import { getServingIdentity, isServingImpersonating, onServingIdentityChange } from "../dag-sync/serving-identity";
import { fromRest, toRest } from "../island-store/at-rest";

export interface PackCache {
  get(name: string): Promise<unknown[] | undefined>;
  /** `size`: the pack's decoded NDJSON length, when known (what the
   *  persistent cache's bound counts) */
  put(name: string, rows: unknown[], size?: number): Promise<void>;
  /** forget every pack (a change of who the data is read as) */
  clear?(): void;
  /** kept beyond this page (IndexedDB): what a prefetch is worth doing for */
  persistent?: boolean;
}

/** No cache at all. */
export const noPackCache: PackCache = {
  async get() { return undefined; },
  async put() {},
};

/** The memory cache's bound, in rows of all its packs together: about
 *  200 MB of parsed rows at ~500 bytes each. Rows, not bytes: a parsed
 *  row's size is not known without walking it, and a pack's row count is. */
export const MEMORY_PACK_ROWS = 400_000;

/** In memory, for one page's life — kept across a reconnect (the new sync
 *  client takes it over) and BOUNDED: past `maxRows` the packs used least
 *  recently go first. A pack bigger than the whole bound is not kept. */
export function memoryPackCache(maxRows = MEMORY_PACK_ROWS): PackCache & { size(): number; rows(): number; clear(): void } {
  const m = new Map<string, unknown[]>(); // insertion order = least recently used first
  let total = 0;
  const drop = (name: string) => {
    const had = m.get(name);
    if (had) { total -= had.length; m.delete(name); }
  };
  return {
    async get(name) {
      const rows = m.get(name);
      if (rows) { m.delete(name); m.set(name, rows); } // used: now the newest
      return rows;
    },
    async put(name, rows) {
      drop(name);
      if (rows.length > maxRows) return;
      m.set(name, rows);
      total += rows.length;
      for (const oldest of m.keys()) {
        if (total <= maxRows) break;
        drop(oldest);
      }
    },
    clear() { m.clear(); total = 0; },
    size: () => m.size,
    rows: () => total,
  };
}

/** THE PERSISTENT PACK STORE (B4; Tom 2026-10-12: on before release). A
 *  pack's signed link changes with every manifest, so the browser's HTTP
 *  cache never hits; this keeps the DECODED rows (VC-UI's timing on a 10k
 *  island: 12 ms to read back, against 24 ms for gzip bytes plus inflate and
 *  parse) in IndexedDB, keyed by the pack's name (its content address).
 *
 *  - One database per (user, organisation): the organisation is
 *    /auth/user's `organization_id`, the one the server's checks use. With
 *    none, nothing is stored (memory only). An organisation switch keeps the
 *    other organisation's database for coming back (VC-core 2026-10-12);
 *    sign-out, or another user, deletes the previous user's databases.
 *  - Bounded by bytes, counted as the packs' decoded NDJSON length (larger
 *    than what a compressing browser stores: safe everywhere); past the
 *    bound the packs used least recently go first.
 *  - A cached pack is used only when the open lists it: the loader asks for
 *    the names the manifest names, so access is checked by the server on
 *    every open. */
export const PERSISTENT_PACK_BYTES = 200 * 1024 * 1024;

const PACKS = "packs";
const META = "meta"; // name -> {bytes, used}
const PREFIX = "sp-state-packs:";
const REGISTRY = "sp-state-packs:databases"; // localStorage: the names opened here
const LAST_USER = "sp-state-packs:last-user";

function dbName(userId: string, organisationId: string): string {
  return `${PREFIX}${userId}:${organisationId}`;
}

function remember(name: string): void {
  try {
    const list = JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[];
    if (!list.includes(name)) localStorage.setItem(REGISTRY, JSON.stringify([...list, name]));
  } catch { /* no storage: the databases list below still finds them */ }
}

let persistAsked = false;
/** Ask once per page that the browser keep this origin's storage. */
function askPersist(): void {
  if (persistAsked) return;
  persistAsked = true;
  try { void navigator.storage?.persist?.().catch(() => undefined); } catch { /* not offered */ }
}

/** Users signed out in this page: their pack databases never open again
 *  here — a prefetch or a fetch the page still runs before its redirect
 *  would otherwise re-create one the sign-out deleted (security, VC-e2e
 *  tier 1, 2026-10-26). */
const signedOut = new Set<string>();
export function closePackCachesOf(userId: string): void {
  if (userId) signedOut.add(userId);
}

/** IndexedDB, one database per (user, organisation), LRU by bytes. */
export function indexedDbPackCache(userId: string, organisationId: string, maxBytes = PERSISTENT_PACK_BYTES): PackCache {
  let opened: Promise<IDBDatabase | null> | null = null;
  const name = dbName(userId, organisationId);
  const open = (): Promise<IDBDatabase | null> => {
    if (opened) return opened;
    opened = new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined" || signedOut.has(userId)) return resolve(null);
        const req = indexedDB.open(name, 2);
        req.onupgradeneeded = () => {
          const db = req.result;
          // version 1 kept rows only, unbounded: start again with the bound
          for (const s of Array.from(db.objectStoreNames)) db.deleteObjectStore(s);
          db.createObjectStore(PACKS);
          db.createObjectStore(META);
        };
        req.onsuccess = () => {
          const db = req.result;
          // signed out while it opened: not used
          if (signedOut.has(userId)) { db.close(); opened = null; return resolve(null); }
          // a deletion (sign-out, another user) waits for every open
          // connection to close: close at once, and open again if used
          // (never for a user signed out here)
          db.onversionchange = () => { db.close(); opened = null; };
          remember(name); askPersist(); resolve(db);
        };
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return opened;
  };
  const done = (t: IDBTransaction) => new Promise<void>((ok) => { t.oncomplete = () => ok(); t.onerror = t.onabort = () => ok(); });
  const read = <T>(r: IDBRequest<T>) => new Promise<T | undefined>((ok) => { r.onsuccess = () => ok(r.result); r.onerror = () => ok(undefined); });
  return {
    async get(pack) {
      const db = await open();
      if (!db) return undefined;
      try {
        const t = db.transaction([PACKS, META], "readwrite");
        const kept = await read(t.objectStore(PACKS).get(pack));
        if (kept !== undefined) {
          const meta = await read(t.objectStore(META).get(pack));
          if (meta) t.objectStore(META).put({ ...(meta as object), used: Date.now() }, pack); // used: now the newest
        }
        await done(t);
        const rows = await fromRest<unknown[]>(kept);
        return Array.isArray(rows) ? rows : undefined;
      } catch {
        return undefined;
      }
    },
    async put(pack, rows, size) {
      const bytes = size ?? JSON.stringify(rows).length;
      if (bytes > maxBytes) return;
      let value: unknown;
      try { value = await toRest(rows); } catch { return; }
      const db = await open();
      if (!db) return;
      try {
        const t = db.transaction([PACKS, META], "readwrite");
        t.objectStore(PACKS).put(value, pack);
        t.objectStore(META).put({ bytes, used: Date.now() }, pack);
        const metaStore = t.objectStore(META);
        const keys = (await read(metaStore.getAllKeys())) ?? [];
        const metas = ((await read(metaStore.getAll())) ?? []) as { bytes: number; used: number }[];
        let total = metas.reduce((a, m) => a + (m?.bytes ?? 0), 0);
        const oldestFirst = keys.map((k, i) => ({ k, m: metas[i] })).sort((a, b) => (a.m?.used ?? 0) - (b.m?.used ?? 0));
        for (const { k, m } of oldestFirst) {
          if (total <= maxBytes) break;
          if (k === pack) continue;
          t.objectStore(PACKS).delete(k);
          metaStore.delete(k);
          total -= m?.bytes ?? 0;
        }
        await done(t);
      } catch { /* quota or a broken database: not cached */ }
    },
  };
}

/** Memory in front of the persistent store: a pack read from IndexedDB is
 *  kept in memory too, a pack fetched goes to both. */
export function layeredPackCache(front: PackCache, back: PackCache | null): PackCache {
  if (!back) return front;
  return {
    async get(name) {
      const hit = await front.get(name);
      if (hit) return hit;
      const stored = await back.get(name);
      if (stored) await front.put(name, stored);
      return stored;
    },
    async put(name, rows, size) {
      await Promise.all([front.put(name, rows, size), back.put(name, rows, size)]);
    },
    clear() { front.clear?.(); },
    persistent: true,
  };
}

const userOf = (identity: string | null | undefined) => (identity ? identity.slice(0, identity.lastIndexOf("|")) : "");
const orgOf = (identity: string | null | undefined) => (identity ? identity.slice(identity.lastIndexOf("|") + 1) : "");

/** The pack cache for who the data is read as: memory, over IndexedDB when
 *  the session names its organisation. While impersonating, memory only:
 *  nothing is kept in the browser's storage for an impersonated person
 *  (VC-Authz, 2026-10-19; the island store's rule), and so no prefetch. */
export function packCacheFor(identity: string | null | undefined, impersonating = isServingImpersonating()): PackCache {
  const user = userOf(identity);
  const org = orgOf(identity);
  return layeredPackCache(memoryPackCache(), user && org && !impersonating ? indexedDbPackCache(user, org) : null);
}

async function databaseNames(): Promise<string[]> {
  const names = new Set<string>();
  try { for (const n of JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[]) names.add(n); } catch { /* none */ }
  try {
    const listed = await (indexedDB as unknown as { databases?: () => Promise<{ name?: string }[]> }).databases?.();
    for (const d of listed ?? []) if (d.name?.startsWith(PREFIX)) names.add(d.name);
  } catch { /* not offered */ }
  return Array.from(names);
}

/** Delete every pack database of `userId` (all its organisations), or only
 *  those of organisations `keep` leaves out. */
/** Delete `userId`'s pack databases (but those of `keep` organisations).
 *  True when every one known was confirmed deleted. A deletion blocked (a
 *  connection elsewhere stays open) or failed is not: its name stays in the
 *  registry, so a later clean-up still finds it without enumeration
 *  (Codex F6, 2026-10-26). */
export async function deletePackCachesOf(userId: string, keep?: string[]): Promise<boolean> {
  if (!userId || typeof indexedDB === "undefined") return true;
  const prefix = `${PREFIX}${userId}:`;
  const mine = (await databaseNames()).filter((n) => n.startsWith(prefix) && !keep?.includes(n.slice(prefix.length)));
  const deleted = await deleteDatabases(mine);
  try {
    const left = (JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[]).filter((n) => !deleted.includes(n));
    localStorage.setItem(REGISTRY, JSON.stringify(left));
  } catch { /* none */ }
  return deleted.length === mine.length;
}

/** Each database deleted, and the names of those CONFIRMED deleted
 *  (onsuccess); blocked, failed or thrown is unknown, never confirmed. */
export async function deleteDatabases(names: string[]): Promise<string[]> {
  const done = await Promise.all(names.map((n) => new Promise<string | null>((ok) => {
    try {
      const r = indexedDB.deleteDatabase(n);
      r.onsuccess = () => ok(n);
      r.onerror = r.onblocked = () => ok(null);
    } catch { ok(null); }
  })));
  return done.filter((n): n is string => !!n);
}

let lastUser: string | null = null;
/** Sign-out, or another user signing in, deletes the previous user's pack
 *  databases; an organisation switch by the same user keeps them. Also run
 *  at the first identity of a page: a previous user who never signed out
 *  here (a closed tab) is found through the last user remembered. */
async function onIdentity(identity: string | null): Promise<void> {
  const user = userOf(identity);
  let previous = lastUser;
  if (previous === null) {
    try { previous = localStorage.getItem(LAST_USER); } catch { previous = null; }
  }
  if (previous && previous !== user) await deletePackCachesOf(previous);
  lastUser = user;
  try {
    if (user) localStorage.setItem(LAST_USER, user);
    else localStorage.removeItem(LAST_USER);
  } catch { /* none */ }
}
onServingIdentityChange((identity) => { void onIdentity(identity); });

/** Sign-out: the current user's pack databases go before the page leaves
 *  (at most `waitMs`, so a slow browser never holds the sign-out). */
export async function forgetPackCachesAtSignOut(waitMs = 1500): Promise<void> {
  const user = userOf(getServingIdentity()) || lastUser || "";
  closePackCachesOf(user);
  await Promise.race([deletePackCachesOf(user), new Promise<void>((ok) => setTimeout(ok, waitMs))]);
  lastUser = "";
  try { localStorage.removeItem(LAST_USER); } catch { /* none */ }
}

/** tests only */
export function resetPackCacheIdentityForTests(): void {
  lastUser = null;
  signedOut.clear();
  persistAsked = false;
}
